import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { taskAPI, teamAPI, authAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Spinner, Badge } from '../components/UI';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Legend,
} from 'recharts';

// Theme-paired chart palettes. Bright on dark, saturated on light.
const STATUS_COLORS_DARK   = { 'To Do': '#60a5fa', 'In Progress': '#fbbf24', 'Completed': '#34d399' };
const STATUS_COLORS_LIGHT  = { 'To Do': '#2563eb', 'In Progress': '#d97706', 'Completed': '#059669' };
const MANAGER_FILL_DARK = '#60a5fa';
const MANAGER_FILL_LIGHT = '#2563eb';
const RECRUITER_FILL_DARK = '#34d399';
const RECRUITER_FILL_LIGHT = '#059669';

function StatBox({ label, value, sub, icon, color = 'indigo' }) {
  const colors = {
    indigo: 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400',
    blue:   'bg-blue-500/10 border-blue-500/20 text-blue-400',
    green:  'bg-green-500/10 border-green-500/20 text-green-400',
    red:    'bg-red-500/10 border-red-500/20 text-red-400',
    yellow: 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400',
  };
  return (
    <div className={`rounded-xl border p-5 ${colors[color]}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xl">{icon}</span>
        <span className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>{value}</span>
      </div>
      <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{label}</p>
      {sub && <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{sub}</p>}
    </div>
  );
}

export default function TeamDashboard() {
  const { user, isSuperAdmin, hasRole } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const STATUS_COLORS  = isDark ? STATUS_COLORS_DARK  : STATUS_COLORS_LIGHT;
  const CHART_INK      = isDark ? '#94a3b8' : '#64748b';
  const TOOLTIP_BG     = isDark ? '#1e293b' : '#ffffff';
  const TOOLTIP_BORDER = isDark ? '#334155' : '#e2e8f0';
  const TOOLTIP_FG     = isDark ? '#ffffff' : '#0f172a';
  const MANAGER_FILL   = isDark ? MANAGER_FILL_DARK   : MANAGER_FILL_LIGHT;
  const RECRUITER_FILL = isDark ? RECRUITER_FILL_DARK : RECRUITER_FILL_LIGHT;

  const [taskStats, setTaskStats]   = useState(null);
  const [teamStats, setTeamStats]   = useState(null);
  const [myTeams, setMyTeams]       = useState([]);
  const [recentTasks, setRecentTasks] = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const tStats = await taskAPI.getDashboardStats();
        setTaskStats(tStats.data.stats);

        const tasks = await taskAPI.getAll({ limit: 5 });
        setRecentTasks(tasks.data.tasks);

        if (isSuperAdmin()) {
          // /teams/stats returns { stats: {totals}, teams: [{ id, name, memberCount, managerCount, recruiterCount }] }
          // — the per-team list sits next to `stats`, not inside it.
          const ts = await teamAPI.getStats();
          setTeamStats({ ...(ts.data.stats || {}), teams: ts.data.teams || [] });
        } else {
          const mt = await teamAPI.getAll({ limit: 20 });
          setMyTeams(mt.data.teams);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [isSuperAdmin]);

  if (loading) return <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>;

  const pieData = taskStats ? [
    { name: 'To Do',       value: taskStats.todo       },
    { name: 'In Progress', value: taskStats.inProgress },
    { name: 'Completed',   value: taskStats.completed  },
  ].filter(d => d.value > 0) : [];

  const teamBarData = isSuperAdmin() && teamStats?.teams
    ? teamStats.teams.slice(0, 6).map(t => ({
        name: t.name,
        managers:   t.managerCount   ?? (t.members || []).filter(m => m.TeamMember?.roleInTeam === 'manager').length,
        recruiters: t.recruiterCount ?? (t.members || []).filter(m => m.TeamMember?.roleInTeam === 'recruiter').length,
      }))
    : [];

  const PRIORITY_COLOR_BADGE = { High: 'danger', Medium: 'warning', Low: 'info' };
  const STATUS_COLOR_BADGE   = { 'To Do': 'default', 'In Progress': 'warning', 'Completed': 'success' };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-white">
          Team Dashboard — {user?.name?.split(' ')[0]} 👋
        </h1>
        <p className="text-slate-400 mt-1 text-sm">
          {isSuperAdmin() ? 'Full system overview' : hasRole(['RecruiterManager']) ? 'Your teams & tasks overview' : 'Your personal task overview'}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatBox label="Total Tasks"   value={taskStats?.total      || 0} icon="📋" color="indigo" />
        <StatBox label="To Do"         value={taskStats?.todo        || 0} icon="🔵" color="blue" />
        <StatBox label="In Progress"   value={taskStats?.inProgress  || 0} icon="🟡" color="yellow" />
        <StatBox label="Completed"     value={taskStats?.completed   || 0} icon="✅" color="green" />
        <StatBox label="Overdue"       value={taskStats?.overdue     || 0} icon="⚠️" color="red"
          sub={taskStats?.overdue > 0 ? 'Needs attention!' : 'All on track'} />
      </div>

      {isSuperAdmin() && teamStats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatBox label="Total Teams"      value={teamStats.totalTeams      || 0} icon="🏆" color="indigo" />
          <StatBox label="Total Managers"   value={teamStats.totalManagers   || 0} icon="👔" color="blue" />
          <StatBox label="Total Recruiters" value={teamStats.totalRecruiters || 0} icon="🤝" color="green" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl p-5 border" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-subtle)' }}>
          <h3 className="font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Task Status Distribution</h3>
          {pieData.length > 0 ? (
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="w-full sm:w-[55%]">
              <ResponsiveContainer width="100%" height={180} minWidth={0}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} dataKey="value" paddingAngle={3}>
                    {pieData.map(entry => (
                      <Cell key={entry.name} fill={STATUS_COLORS[entry.name]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: TOOLTIP_BG, border: `1px solid ${TOOLTIP_BORDER}`, color: TOOLTIP_FG, borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
              </div>
              <div className="space-y-2">
                {pieData.map(item => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ background: STATUS_COLORS[item.name] }} />
                    <span className="text-slate-400 text-xs">{item.name}:</span>
                    <span className="text-white text-xs font-semibold">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <p className="text-slate-500 text-center py-10">No tasks yet</p>}
        </div>

        {isSuperAdmin() && teamBarData.length > 0 ? (
          <div className="rounded-xl p-5 border" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-subtle)' }}>
            <h3 className="font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Team Composition</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={teamBarData} barSize={14} margin={{ top: 5, right: 5, left: 0, bottom: 30 }}>
                <XAxis dataKey="name" tick={{ fill: CHART_INK, fontSize: 11 }} angle={-30} textAnchor="end" interval={0} height={50} />
                <YAxis tick={{ fill: CHART_INK, fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: TOOLTIP_BG, border: `1px solid ${TOOLTIP_BORDER}`, color: TOOLTIP_FG, borderRadius: 8 }} />
                <Legend wrapperStyle={{ color: CHART_INK, fontSize: 12 }} />
                <Bar dataKey="managers"   fill={MANAGER_FILL}   radius={[4, 4, 0, 0]} />
                <Bar dataKey="recruiters" fill={RECRUITER_FILL} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="rounded-xl p-5 border" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-subtle)' }}>
            <h3 className="font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>My Teams</h3>
            {myTeams.length === 0 ? (
              <p className="text-slate-500 text-center py-10">Not assigned to any team</p>
            ) : (
              <div className="space-y-3">
                {myTeams.map(team => {
                  const managers   = (team.members || []).filter(m => m.TeamMember?.roleInTeam === 'manager');
                  const recruiters = (team.members || []).filter(m => m.TeamMember?.roleInTeam === 'recruiter');
                  return (
                    <div key={team.id} className="bg-slate-800 rounded-lg px-4 py-3 flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{team.name}</p>
                        <p className="text-slate-400 text-xs truncate">{managers.length} managers · {recruiters.length} recruiters</p>
                      </div>
                      <Link to="/teams" className="text-xs text-indigo-400 hover:text-indigo-300 flex-shrink-0">View →</Link>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="rounded-xl p-5 border" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-subtle)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Recent Tasks</h3>
          <Link to="/tasks" className="text-indigo-400 text-sm hover:text-indigo-300">View all →</Link>
        </div>
        {recentTasks.length === 0 ? (
          <p className="text-slate-500 text-center py-6">No tasks yet</p>
        ) : (
          <div className="space-y-2">
            {recentTasks.map(task => {
              const isOverdue = task.deadline && new Date(task.deadline) < new Date() && task.status !== 'Completed';
              return (
                <div key={task.id} className="flex flex-wrap items-center gap-3 bg-slate-800 rounded-lg px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{task.title}</p>
                    <p className="text-slate-400 text-xs">
                      {task.assignedToUser?.name || task.assignedToTeam?.name || 'Unassigned'}
                      {task.requirement && ` · ${task.requirement.jobTitle}`}
                    </p>
                  </div>
                  <Badge variant={PRIORITY_COLOR_BADGE[task.priority]} size="sm">{task.priority}</Badge>
                  <Badge variant={STATUS_COLOR_BADGE[task.status]} size="sm">{task.status}</Badge>
                  {task.deadline && (
                    <span className={`text-xs hidden sm:block ${isOverdue ? 'text-red-400' : 'text-slate-500'}`}>
                      {isOverdue ? '⚠ ' : ''}{new Date(task.deadline).toLocaleDateString()}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
